import * as React from 'react'

import { Box, Table, Tag, Text } from '@factorialco/deprecated-design-system'
import { AssessmentsTableEmployeeFragment } from 'generated/resources/graphql'
import groupBy from 'lodash/groupBy'

import { observer } from 'mobx-react'

import createComputed from 'modules/core/hooks/createComputed'
import i18n from 'modules/core/lib/i18n'

import { useAssessments } from 'modules/competencies/pages/assessmentsContext'
import type { AssessmentGroups } from 'modules/competencies/store/collections/assessmentGroups'
import type { Assessment } from 'modules/competencies/store/models/Assessment'
import type { AssessmentGroup as AssessmentGroupModel } from 'modules/competencies/store/models/AssessmentGroup'

import AssessmentGroupActions from '../_AssessmentGroupActions'
import AssessmentTable from '../_AssessmentTable'
import AssessmentsNavigator from './_AssessmentsNavigator'

interface Props {
  employee: AssessmentsTableEmployeeFragment
  isManaged: boolean
}

const useUserAssessmentGroups = createComputed((assessmentGroups: AssessmentGroups, id: number) => {
  return assessmentGroups
    .filter({ target_id: id })
    .sort(
      (a: AssessmentGroupModel, b: AssessmentGroupModel) =>
        a.attributes.created_at - b.attributes.created_at
    )
})

const AssessmentGroup = ({ employee, isManaged }: Props) => {
  const { assessmentGroups, assessments } = useAssessments()
  const userAssessmentGroups = useUserAssessmentGroups(assessmentGroups, employee.access.id).get()
  const lastAssessmentPosition = userAssessmentGroups.length - 1
  const [currentAssessmentPosition, setCurrentAssessmentPosition] =
    React.useState(lastAssessmentPosition)

  React.useEffect(() => {
    setCurrentAssessmentPosition(lastAssessmentPosition)
  }, [lastAssessmentPosition])

  const currentAssessmentGroup = userAssessmentGroups[currentAssessmentPosition]

  if (!currentAssessmentGroup) return null

  const currentDate = currentAssessmentGroup.attributes.created_at

  const currentAssessments = assessments.filter({
    assessment_group_id: currentAssessmentGroup.attributes.id,
  })

  const groupedAssessments: {
    [parentId: string]: Assessment[]
  } = groupBy(currentAssessments, (assessment) => {
    if (!assessment.competency.parent) return assessment.competency.attributes.id

    return assessment.competency.parent.attributes.id
  })

  const isDraft = currentAssessmentGroup.attributes.status === 'draft'
  const tagColor = isDraft ? 'champagne' : 'lime'

  const description = isDraft
    ? i18n.t('competencies_v2.assessments.labels.draft')
    : i18n.t('competencies_v2.assessments.labels.published')

  return (
    <Table.Row background="grey100">
      <Table.Cell colSpan={12}>
        <Box flexDirection="column" gap="s16" width="full">
          <Box flexDirection="row" justifyContent="spaceBetween" aria-label="assessment-navigator">
            <Box flexDirection="row" justifyContent="flexStart" gap="s8" alignItems="center">
              <AssessmentsNavigator
                currentPosition={currentAssessmentPosition}
                setCurrentPosition={setCurrentAssessmentPosition}
                lastPosition={lastAssessmentPosition}
                currentDate={currentDate}
              />
              <Tag leftItem="circle" capitalize showCircle type={tagColor}>
                {description}
              </Tag>
            </Box>
            {isManaged && (
              <AssessmentGroupActions assessmentGroup={currentAssessmentGroup} isDraft={isDraft} />
            )}
          </Box>
          {Object.keys(groupedAssessments).length === 0 && (
            <Box flexDirection="row" justifyContent="center" alignItems="center">
              <Text.Normal weight="medium">
                {i18n.t('competencies_v2.assessments.no_competencies_message')}
              </Text.Normal>
            </Box>
          )}
          {Object.keys(groupedAssessments).map((parentId) => {
            const assessments = groupedAssessments[+parentId] as Assessment[]
            const parentAssessment = assessments.find(
              (a) => a.competency.attributes.id === +parentId
            )
            const childAssessments = assessments.filter(
              (a) => a.competency.parent?.attributes.id === +parentId
            )

            return (
              <AssessmentTable
                key={parentId}
                parentAssessment={parentAssessment}
                childAssessments={childAssessments}
                canEdit={isManaged}
                isDraft={isDraft}
              />
            )
          })}{' '}
        </Box>
      </Table.Cell>
    </Table.Row>
  )
}

export default observer(AssessmentGroup)
